import type { Game } from '../core/game';
import type { Affix, MachineDef, MasterDie, NodeDef } from '../core/types';
import { BALANCE } from '../data/balance';
import { NODES } from '../data/nodes';
import { callGame, hasMethod, nodeLevel } from './gameApi';

/**
 * Adaptateur « Abîme » — v0.46.0 (A94, lot B : écrans du mode infini).
 *
 * Même contrat que `ui/gameApi.ts` : **rien** de ce que livre le lot A (cœur)
 * n'est supposé présent. Chaque lecture passe par une fonction gardée qui rend
 * une valeur neutre quand la donnée ou la méthode manque — un dé de campagne
 * (sans niveau ni Matériau) et un arbre sans Abîme se comportent alors
 * exactement comme avant la v0.46.0, et les écrans restent verts avant comme
 * après la fusion des deux lots.
 *
 * Lu ici : la branche `abyss` de l'arbre (portes, Expéditions, répétable),
 * l'Artisan des dés, `expeditionGoldRef`, les Corrupted GachaDice, le niveau /
 * Matériau / Enrichi / marques de Forge d'un dé, et les deux événements
 * `abyss` et `purchase`.
 */

/** Identifiant de branche de l'Abîme dans `NodeDef.branch` (absent des types avant le lot A). */
export const ABYSS_BRANCH = 'abyss';
/** Le nœud qui ouvre l'Abîme, la Forge et les Corrupted GachaDice. */
export const ARTISAN_NODE = 'aby_artisan';

type Rec = Record<string, unknown>;

function rec(v: unknown): Rec {
  return typeof v === 'object' && v !== null ? (v as Rec) : {};
}

function finite(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

function balanceNumber(key: string, fallback: number): number {
  return finite((BALANCE as unknown as Rec)[key]) ?? fallback;
}

// ───────────────────────────────────────────────────────────── L'arbre

/** Ce nœud appartient-il à la branche de l'Abîme ? */
export function isAbyssNode(def: { branch: string }): boolean {
  return (def.branch as string) === ABYSS_BRANCH;
}

/** L'Artisan des dés est-il acquis ? C'est lui qui fait exister le couloir. */
export function hasArtisan(game: Game): boolean {
  return nodeLevel(game, ARTISAN_NODE) >= 1;
}

/**
 * Nœuds à dessiner : toute la liste, sauf la branche de l'Abîme tant que
 * l'Artisan des dés vaut 0 (A94 : le couloir « n'existe pas » avant).
 */
export function visibleNodes(game: Game): readonly NodeDef[] {
  return hasArtisan(game) ? NODES : NODES.filter((n) => !isAbyssNode(n));
}

/** Meilleur niveau d'Expédition atteint (`stats.bestLevel`), 0 s'il manque. */
export function bestLevelOf(game: Game): number {
  const stats = rec(rec(game.state).stats);
  return Math.max(0, Math.floor(finite(stats.bestLevel) ?? 0));
}

/** Nombre d'Expéditions du prix d'un niveau de l'Abîme (`costExpeditions`), ou `null`. */
export function costExpeditionsOf(def: NodeDef): number | null {
  const n = finite(rec(def).costExpeditions);
  return n !== null && n > 0 ? n : null;
}

/** Le nœud est-il répétable sans limite (*Sans fond*, `maxLevel` infini) ? */
export function isRepeatable(def: NodeDef): boolean {
  return rec(def).repeatable === true || !Number.isFinite(def.maxLevel);
}

export interface GateView {
  /** Niveau d'Expédition à atteindre pour acheter le prochain niveau du nœud. */
  level: number;
  /** Porte déjà franchie (`bestLevel` ≥ `level`) ? */
  open: boolean;
}

/**
 * Porte du **prochain niveau achetable** d'un nœud de l'Abîme. `game.abyssGate`
 * quand le cœur le fournit ; sinon recalculée depuis `NodeDef.gate`
 * (`level + step × niveau possédé`) et `stats.bestLevel`. `null` hors Abîme ou
 * sans donnée.
 */
export function abyssGateOf(game: Game, nodeId: string): GateView | null {
  if (hasMethod(game, 'abyssGate')) {
    const g = rec(callGame(game, 'abyssGate', nodeId));
    const level = finite(g.level);
    if (level !== null) return { level, open: g.open === true };
    return null;
  }
  const def = NODES.find((n) => n.id === nodeId);
  const gate = def ? rec(rec(def).gate) : null;
  const base = gate ? finite(gate.level) : null;
  if (base === null) return null;
  const step = finite(gate?.step) ?? 0;
  const level = base + step * nodeLevel(game, nodeId);
  return { level, open: bestLevelOf(game) >= level };
}

/** Niveau d'ouverture quand la raison de refus est `'gate'` (`gateLevel`), sinon `null`. */
export function gateLevelOf(av: { reason?: string; gateLevel?: number } | undefined): number | null {
  if (!av || av.reason !== 'gate') return null;
  return finite(av.gateLevel) ?? null;
}

/** Cette disponibilité est-elle un refus de porte (`reason === 'gate'`) ? */
export function isGateReason(av: { reason?: string } | undefined): boolean {
  return av?.reason === 'gate';
}

/**
 * Or d'une Expédition du niveau `level` (niveau courant par défaut) :
 * `game.expeditionGoldRef`, ou `null` tant que le cœur ne le fournit pas.
 */
export function expeditionGoldRefOf(game: Game, level?: number): number | null {
  if (!hasMethod(game, 'expeditionGoldRef')) return null;
  const v = finite(level === undefined ? callGame(game, 'expeditionGoldRef') : callGame(game, 'expeditionGoldRef', level));
  return v !== null && v > 0 ? v : null;
}

// ───────────────────────────────────────────────── Textes (toasts, panneau)

/** Un niveau de nœud dont la porte vient de s'ouvrir (événement `abyss`). */
export interface AbyssOpened {
  nodeId: string;
  level: number;
}

/** Lit la charge utile de l'événement `abyss` (`{ opened: [...] }`), sans rien supposer. */
export function abyssOpenedOf(payload: unknown): AbyssOpened[] {
  const list = rec(payload).opened;
  if (!Array.isArray(list)) return [];
  const out: AbyssOpened[] = [];
  for (const raw of list) {
    const o = rec(raw);
    const level = finite(o.level);
    if (typeof o.nodeId !== 'string' || level === null) continue;
    out.push({ nodeId: o.nodeId, level: Math.max(1, Math.floor(level)) });
  }
  return out;
}

/** « L'Abîme s'ouvre : Matériau » / « … : Grimoire des nombres · niveau 2 ». */
export function abyssOpenedText(opened: AbyssOpened, nodeName: string): string {
  return `L'Abîme s'ouvre : ${nodeName}${opened.level > 1 ? ` · niveau ${opened.level}` : ''}`;
}

/** Toast du premier achat de l'Artisan des dés (acheté ou offert à la victoire). */
export const ARTISAN_TOAST = 'Artisan des dés : la Forge et les Corrupted GachaDice sont ouvertes';

/** Nom français d'un nœud de l'arbre (identifiant brut si inconnu). */
export function nodeNameOf(nodeId: string): string {
  return NODES.find((n) => n.id === nodeId)?.name ?? nodeId;
}

/**
 * Abonnement à un événement du cœur **avec sa charge utile** (`abyss`,
 * `forge`, `purchase`…). `onGameEvent` de `ui/gameApi.ts` ne la transmet pas ;
 * `Game.on` accepte n'importe quelle clé à l'exécution.
 */
export function onGameEventWith(game: Game, event: string, fn: (payload: unknown) => void): () => void {
  const on = (game as unknown as { on?: (ev: string, f: (p: unknown) => void) => () => void }).on;
  if (typeof on !== 'function') return () => undefined;
  try {
    const off = on.call(game, event, fn);
    return typeof off === 'function' ? off : () => undefined;
  } catch {
    return () => undefined;
  }
}

// ───────────────────────────────────────── Corrupted GachaDice et Forge

/** La Forge (et les Corrupted GachaDice) existe-t-elle ? `game.hasForge()`, faux sans le cœur. */
export function hasForge(game: Game): boolean {
  return hasMethod(game, 'hasForge') && callGame(game, 'hasForge') === true;
}

/** Cette machine est-elle une Corrupted GachaDice (`MachineDef.corrupted`) ? */
export function isCorruptedMachine(machine: MachineDef): boolean {
  return rec(machine).corrupted === true;
}

/** Machines Corrupted disponibles (`game.corruptedMachines()`), vide sans la Forge. */
export function corruptedMachinesOf(game: Game): MachineDef[] {
  if (!hasForge(game)) return [];
  const list = callGame(game, 'corruptedMachines');
  return Array.isArray(list) ? (list as MachineDef[]).filter((m) => typeof m?.id === 'string') : [];
}

/** Prix du prochain dé d'une Corrupted GachaDice (`corruptedUnitCost`), ou `null`. */
export function corruptedUnitCostOf(game: Game, machineId: string): number | null {
  const v = finite(callGame(game, 'corruptedUnitCost', machineId));
  return v !== null && v >= 0 ? v : null;
}

/** Dés déjà tirés à cette Corrupted GachaDice (`state.corruptedPurchases[id]`, permanent). */
export function corruptedPurchasesOf(game: Game, machineId: string): number {
  const map = rec(rec(game.state).corruptedPurchases);
  return Math.max(0, Math.floor(finite(map[machineId]) ?? 0));
}

// ──────────────────────────────────────────────── Niveau et Matériau d'un dé

/** Niveau d'un dé (`MasterDie.level`) : 0 pour un dé de campagne. */
export function dieLevelOf(die: MasterDie): number {
  const n = finite(rec(die).level);
  return n !== null && n > 0 ? Math.floor(n) : 0;
}

/** Matériau d'un dé (`MasterDie.material`), ou `null`. */
export function dieMaterialOf(die: MasterDie): string | null {
  const m = rec(die).material;
  return typeof m === 'string' && m !== '' ? m : null;
}

/**
 * Le dé vient-il des Corrupted GachaDice (A98 : `MasterDie.corrupted`) ? Seuls
 * ces dés se forgent et portent le Mult_Corrupted sur leurs dégâts plats ; un dé de campagne
 * (sans la marque, ou avec un cœur plus ancien) ne l'est pas.
 */
export function isCorruptedDie(die: MasterDie): boolean {
  return rec(die).corrupted === true;
}

/** Mult_Corrupted des dés Corrupted sur leurs faces (`corruptedFlatFactor`, 5 000 depuis A114), repli à cette valeur. */
export function corruptedFactor(game?: Game | null): number {
  // A111 : la surcharge de debug vit dans les stats dérivées du cœur.
  if (game && hasMethod(game, 'getDerivedStats')) {
    const v = finite((callGame(game, 'getDerivedStats') as { corruptedFlatFactor?: unknown } | null)?.corruptedFlatFactor);
    if (v !== null && v > 0) return v;
  }
  return balanceNumber('corruptedFlatFactor', 5_000);
}

/** Le dé est-il Enrichi ? */
export function isEnriched(die: MasterDie): boolean {
  return rec(die).enriched === true;
}

/** Marques de Forge d'un dé (`MasterDie.forge`) lues sans rien supposer. */
export interface ForgeMarksView {
  /** Index (dans `die.affixes`) du seul affixe encore relançable. */
  rerollSlot: number | null;
  /** Index de l'affixe ajouté par Absolution. */
  absolvedSlot: number | null;
  /** Dé issu d'une Fusion. */
  fused: boolean;
}

export function forgeMarksOf(die: MasterDie): ForgeMarksView {
  const f = rec(rec(die).forge);
  const index = (v: unknown): number | null => {
    const n = finite(v);
    return n !== null && n >= 0 ? Math.floor(n) : null;
  };
  return { rerollSlot: index(f.rerollSlot), absolvedSlot: index(f.absolvedSlot), fused: f.fused === true };
}

/** Rapport de dégâts d'un niveau au suivant (`dieLevelFactor`, 2 depuis A110, 1,5 avant), lu dans l'équilibrage. */
export function dieLevelGrowth(game?: Game | null): number {
  if (game && hasMethod(game, 'getDerivedStats')) {
    const v = finite((callGame(game, 'getDerivedStats') as { dieLevelFactor?: unknown } | null)?.dieLevelFactor);
    if (v !== null && v > 0) return v;
  }
  return balanceNumber('dieLevelFactor', 2);
}

/** Facteur de dégâts d'un niveau : `dieLevelFactor^niveau`, lu dans l'équilibrage. */
export function levelFactorOf(game: Game | null | undefined, die: MasterDie): number {
  if (game && hasMethod(game, 'dieLevelFactor')) {
    const v = finite(callGame(game, 'dieLevelFactor', die));
    if (v !== null && v > 0) return v;
  }
  const level = dieLevelOf(die);
  return level > 0 ? Math.pow(dieLevelGrowth(), level) : 1;
}

/** Plafond de niveau d'un dé (`dieLevelMax()`, 20 + 2 par niveau du Marteau), ou `null`. */
export function dieLevelMaxOf(game: Game): number | null {
  const v = finite(callGame(game, 'dieLevelMax'));
  return v !== null && v > 0 ? v : null;
}

/** Multiplicateur d'un dé Enrichi sur ses affixes (`enrichedAffixFactor`, ×1,5). */
export function enrichedFactor(): number {
  return balanceNumber('enrichedAffixFactor', 1.5);
}

/**
 * Valeur d'un affixe **telle que le dé la porte** (`affixValueOf`) : Enrichi
 * compris. Sans le cœur, le repli multiplie par le facteur d'Enrichi — un dé
 * de campagne n'est jamais touché.
 */
export function affixValueOf(game: Game | null | undefined, die: MasterDie, affix: Affix): number {
  if (game && hasMethod(game, 'affixValueOf')) {
    const v = finite(callGame(game, 'affixValueOf', die, affix));
    if (v !== null) return v;
  }
  return isEnriched(die) ? affix.value * enrichedFactor() : affix.value;
}

// ───────────────────────────────────────────────────────────── Matériaux

/** Un Matériau, tel que `src/data/materials.ts` le décrit (ou le repli local). */
export interface MaterialView {
  id: string;
  name: string;
  /** 1 : tiré dès l'Artisan ; 2 : à partir de Matériologie. */
  tier: number;
  bonus3: string;
  bonus5: string;
}

/**
 * Repli quand `src/data/materials.ts` n'existe pas encore : les six Matériaux
 * de la révision du GD (nom seul ; les bonus sont ceux du module du cœur).
 */
const MATERIAL_FALLBACK: readonly MaterialView[] = [
  { id: 'wood', name: 'Boisé', tier: 1, bonus3: '', bonus5: '' },
  { id: 'bone', name: 'Osseux', tier: 1, bonus3: '', bonus5: '' },
  { id: 'iron', name: 'Ferreux', tier: 1, bonus3: '', bonus5: '' },
  { id: 'rubber', name: 'Caoutchouc', tier: 2, bonus3: '', bonus5: '' },
  { id: 'silicon', name: 'Silicium', tier: 2, bonus3: '', bonus5: '' },
  { id: 'void', name: 'Du Vide', tier: 2, bonus3: '', bonus5: '' },
];

/**
 * Import **dynamique** du module du cœur : `import.meta.glob` ne lie aucun nom
 * à la compilation. Absent, l'objet est vide (le build et `tsc` passent) ;
 * présent après la fusion, il porte `MATERIALS`.
 */
const MATERIAL_MODULES = import.meta.glob('../data/materials.ts', { eager: true }) as Record<string, unknown>;

/** Les Matériaux, dans l'ordre du module du cœur (repli : les six de la révision). */
export function materialList(): readonly MaterialView[] {
  const mod = rec(Object.values(MATERIAL_MODULES)[0]);
  const raw = mod.MATERIALS;
  if (!Array.isArray(raw)) return MATERIAL_FALLBACK;
  const out: MaterialView[] = [];
  for (const m of raw) {
    const o = rec(m);
    if (typeof o.id !== 'string' || typeof o.name !== 'string') continue;
    out.push({
      id: o.id,
      name: o.name,
      tier: finite(o.tier) ?? 1,
      bonus3: typeof o.bonus3 === 'string' ? o.bonus3 : '',
      bonus5: typeof o.bonus5 === 'string' ? o.bonus5 : '',
    });
  }
  return out.length > 0 ? out : MATERIAL_FALLBACK;
}

/** Le Matériau `id` (nom brut du `id` si inconnu : l'écran ne casse jamais). */
export function materialView(id: string): MaterialView {
  return (
    materialList().find((m) => m.id === id) ??
    MATERIAL_FALLBACK.find((m) => m.id === id) ?? { id, name: id, tier: 1, bonus3: '', bonus5: '' }
  );
}
